import { Component, OnInit } from '@angular/core';
import { SearchStateService } from './services/search-state.service';
import { IndexedDBService } from './indexed-db/indexed-db.service';
import { Activity } from '@dal/shared-ui';
import { UploadJob } from './indexed-db/indexed-db.interface';
import { SpinnerService, UsersService } from '@dal/core';
import { map, Observable, tap, of, switchMap } from 'rxjs';
import { BusinessUnitSelectorComponent } from '@dal/shared-ui';
import { MatDialog } from '@angular/material/dialog';
import { UploadWorkerService } from './services/upload-worker.service';
import { JobStatus } from '@dal/async-upload';
@Component({
  selector: 'dal-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit {
  showSideNav: boolean;
  navItems: { name: string; link: string }[] = [
    { name: 'Activities', link: 'activity-queue' },
    { name: 'Manage Image Carousel', link: '' },
    { name: 'Manage Media Portal Lightboxes', link: '' },
    { name: 'Manage Metadata Templates', link: 'template-manager' },
    { name: 'Manage Notifications', link: '' },
    { name: 'Manage Users', link: 'users' },
  ];
  searchTerm: string = '';
  loading$: Observable<number>;
  activities$: Observable<Activity[] | null>;
  constructor(
    private searchStateService: SearchStateService,
    private spinner: SpinnerService,
    private userService: UsersService,
    private dialog: MatDialog,
    private indexedDbService: IndexedDBService,
    private uploadWorkerService: UploadWorkerService
  ) {
    this.showSideNav = false;
    this.loading$ = this.spinner.spinnerCounter$;
  }

  ngOnInit() {
    this.userService
      .getCurrentBusinessUnit()
      .pipe(
        switchMap((businessUnit: any) => {
          if (
            businessUnit?.message?.includes(
              'Please select the business unit to proceed further'
            )
          ) {
            return this.dialog
              .open(BusinessUnitSelectorComponent, {
                data: {
                  dialogHeading: 'Select Your Business Unit:',
                  businessUnits: businessUnit.user.business_unit_roles,
                  name: `${businessUnit.user.firstname} ${businessUnit.user.lastname}`,
                },
                disableClose: true,
              })
              .afterClosed()
              .pipe(
                switchMap((selectedUnitId) => {
                  console.log('Selected Business Unit ID:', selectedUnitId);
                  return this.userService
                    .setCurrentBusinessUnit(selectedUnitId)
                    .pipe(
                      tap((response) =>
                        console.log('Business unit set successfully:', response)
                      )
                    );
                })
              );
          } else {
            return of(null);
          }
        })
      )
      .subscribe({
        next: () => {
          console.log('Business unit is set, app can proceed');
          // Optionally, you can redirect to the desired route here
        },
        error: (err) => {
          console.error('Error during business unit setup:', err);
        },
      });

    // Initialize the upload worker service and subscribe to its events
    this.uploadWorkerService.listenToWorker(); // Listen to worker messages for upload progress and completion

    // Subscribe to upload completion events
    this.uploadWorkerService.completion$.subscribe({
      next: (results) => {
        console.log('✅ Upload completion received:', results);
      },
      error: (error) => {
        console.error('❌ Upload completion error:', error);
      },
    });

    // Subscribe to job completion events to update the UI
    this.uploadWorkerService.jobCompletion$.subscribe({
      next: (jobId) => {
        console.log('✅ Job completed:', jobId);
        // Refresh the activities list to reflect completion
        this.indexedDbService.refreshJobs();
      },
      error: (error) => {
        console.error('❌ Job completion error:', error);
      },
    });
    this.activities$ = this.indexedDbService.jobs$.pipe(
      map((jobs: UploadJob[] | null) => {
        return (jobs ?? [])
          .sort(
            (a, b) =>
              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          ) // ⬅️ sort by date descending
          .map((job) => {
            const isDone = job.status === JobStatus.COMPLETE;
            const isPaused = job.status === JobStatus.PAUSED;
            const isPending = job.status === JobStatus.PENDING;
            const isUploading = job.status === JobStatus.UPLOADING;

            return {
              id: job.id,
              type: 'Bulk Upload',
              isDone: isDone,
              date: new Date(job.createdAt).toLocaleDateString(),
              status: job.status,
              completion: job.completion || 0,
              canPause: isPending || isUploading,
              canResume: isPaused,
              canCancel: isPending || isUploading || isPaused,
            };
          }) as unknown as Activity[];
      })
    );
    this.indexedDbService.refreshJobs(); // Ensure jobs are loaded initially
  }

  deleteActivity(activity: Activity) {
    console.log('delete activity', activity);

    this.indexedDbService.delete(activity.id).subscribe((res) => {
      console.log('Activity deleted successfully:', res);
    });
  }

  pauseJob(activity: Activity) {
    console.log('pause job', activity);

    if (activity && activity.id) {
      this.uploadWorkerService.pauseJob(activity.id).subscribe(() => {
        console.log('Job paused successfully');
      });
    }
  }

  resumeJob(activity: Activity) {
    console.log('resume job', activity);

    if (activity && activity.id) {
      this.uploadWorkerService.resumeJob(activity.id).subscribe(() => {
        console.log('Job resumed successfully');
      });
    }
  }

  cancelJob(activity: Activity) {
    console.log('cancel job', activity);

    if (activity && activity.id) {
      this.uploadWorkerService.cancelJob(activity.id).subscribe(() => {
        console.log('Job canceled successfully');
      });
    }
  }

  toggleSideNav(toggleSideNav: boolean) {
    this.showSideNav = toggleSideNav;
  }

  handleSearch(searchText: string) {
    this.searchTerm = searchText.trim();
    this.searchStateService.updateSearchTerm(this.searchTerm);
  }
}
